// Mirrors the gsm 7 bit part calculation of backend/sms/sms_utils.py
// so the character counter below the message field matches what the
// backend will actually send

// The gsm 7 bit basic character set
const GSM7_BASIC_CHARACTERS =
    "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
    "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

// The gsm 7 bit extended characters which count as two characters
const GSM7_EXTENDED_CHARACTERS = "^{}\\[~]|€";

// The character replacements for characters outside the gsm set
const GSM7_REPLACEMENTS: Record<string, string> = {
    '‘': "'", '’': "'", '‚': "'", '‛': "'",
    '“': '"', '”': '"', '„': '"',
    '–': '-', '—': '-', '−': '-',
    '…': '...',
    '´': "'", '`': "'",
};

// The character limits of a single and a concatenated sms part
export const SMS_SINGLE_PART_LENGTH = 160;
export const SMS_MULTI_PART_LENGTH = 153;

// The shape of a template tag from the tag registry endpoint
export interface SmsTemplateTag {
    key: string;
    label_key: string;
    min_length: number;
    max_length: number;
    message_types: string[];
}

// Normalizes a text to the gsm character set like the backend does
export const normalizeGsm7 = (text: string): string => {
    if (!text) return '';
    let result = '';
    for (const character of text) {
        if (GSM7_BASIC_CHARACTERS.includes(character) || GSM7_EXTENDED_CHARACTERS.includes(character)) {
            result += character;
            continue;
        }
        if (GSM7_REPLACEMENTS[character]) {
            result += GSM7_REPLACEMENTS[character];
            continue;
        }

        // Reduce accented characters to their base character
        const base = character.normalize('NFKD').replace(/[̀-ͯ]/g, '');
        if (base && [...base].every(part => GSM7_BASIC_CHARACTERS.includes(part) || GSM7_EXTENDED_CHARACTERS.includes(part))) {
            result += base;
        }

        // Every other character such as emoji is dropped
    }

    // Collapse any double spaces the removals may have left behind
    return result.replace(/ {2,}/g, ' ');
};

// Calculates the gsm length where extended characters count double
export const calculateSmsLength = (text: string): number => {
    let length = 0;
    for (const character of text || '') {
        length += GSM7_EXTENDED_CHARACTERS.includes(character) ? 2 : 1;
    }
    return length;
};

// Calculates the number of sms parts a text uses
export const calculateSmsParts = (length: number): number => {
    if (length === 0) return 1;
    if (length <= SMS_SINGLE_PART_LENGTH) return 1;
    return Math.ceil(length / SMS_MULTI_PART_LENGTH);
};

// Estimates the minimum and maximum length of a body with tags
export const estimateLengthRange = (body: string, tags: SmsTemplateTag[]): { min: number, max: number } => {
    const tagPattern = /\{([a-z_]+)\}/g;

    // Replace every tag with a single character sentinel first
    const matches = [...(body || '').matchAll(tagPattern)];
    const stripped = (body || '').replace(tagPattern, '§');
    const base = calculateSmsLength(normalizeGsm7(stripped)) - matches.length;

    // Add the registered length range of every used tag
    let min = base;
    let max = base;
    for (const match of matches) {
        const tag = tags.find(candidate => candidate.key === match[1]);
        if (tag) {
            min += tag.min_length;
            max += tag.max_length;
        } else {
            const literal = calculateSmsLength(match[0]);
            min += literal;
            max += literal;
        }
    }

    return { min, max };
};

// Determines the counter warning level for the given maximum length
export const getCounterLevel = (max: number): 'ok' | 'warning' | 'error' => {
    const parts = calculateSmsParts(max);
    if (parts > 1) return 'error';
    if (max > SMS_SINGLE_PART_LENGTH - 10) return 'warning';
    return 'ok';
};
